import React, { useState, useEffect } from 'react';
import Header from '../homepage/header';
import { DIGITAL_TWIN_BASE, DIGITAL_TWIN_API, DIGITAL_TWIN_GRAFANA } from '../../apiConfig';
import './digitaltwin.css';

function DigitalTwin() {
    const [twinOnline, setTwinOnline] = useState(null); // null = checking

    const currentHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const twinUrl = process.env.REACT_APP_DIGITAL_TWIN_URL || (
        typeof window !== 'undefined' && window.location.protocol === 'https:'
            ? `${window.location.origin}/digital-twin-app/`
            : `https://csrl.nitk.ac.in/digital-twin-app/`
    );
    const grafanaUrl = process.env.REACT_APP_DIGITAL_TWIN_GRAFANA_URL || (
        typeof window !== 'undefined' && window.location.protocol === 'https:'
            ? `${window.location.origin}/digital-twin-grafana`
            : `http://${currentHost}:3005`
    );

    const checkServer = async () => {
        try {
            await fetch(twinUrl, {
                method: 'GET',
                mode: 'no-cors',
                signal: AbortSignal.timeout(3000),
            });
            setTwinOnline(true);
        } catch {
            // Fallback check to backend API port 3001
            try {
                const apiRes = await fetch(`${DIGITAL_TWIN_API}/health`, {
                    method: 'GET',
                    signal: AbortSignal.timeout(3000),
                });
                setTwinOnline(apiRes.ok || apiRes.status < 500);
            } catch {
                setTwinOnline(false);
            }
        }
    };

    useEffect(() => {
        checkServer();
        const interval = setInterval(checkServer, 15000);
        return () => clearInterval(interval);
    }, []);

    const statusLabel =
        twinOnline === null ? { text: 'Checking...', cls: 'status-checking' } :
        twinOnline         ? { text: 'Online',       cls: 'status-online'   } :
                              { text: 'Offline',      cls: 'status-offline'  };

    return (
        <div className="digitaltwin-page">
            <Header />

            {/* ── Header Banner ─────────────────────────────────────────── */}
            <div className="dt-page-header">
                <div className="dt-page-header-text">
                    <div className="dt-title-row">
                        <h2>Cyber-Physical Digital Twin Platform</h2>
                        <span className={`server-status-badge ${statusLabel.cls}`}>
                            ⚡ Digital Twin Engine: {statusLabel.text}
                        </span>
                    </div>
                    <p>Real-Time Telemetry Simulation, Smart Hydroponics Control &amp; Hydro-Generator Engine Monitoring</p>
                </div>
                <div className="dt-header-actions">
                    <a
                        href={twinUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="open-external-btn"
                    >
                        ↗ Open Full Screen
                    </a>
                </div>
            </div>

            {/* ── Main Content Area ─────────────────────────────────────── */}
            <div className="dt-content-container">
                {twinOnline === false ? (
                    <div className="dt-offline-screen">
                        <div className="offline-icon">⚙️</div>
                        <h3>Digital Twin Engine is Currently Offline</h3>
                        <p>
                            The Digital Twin application engine service is not detected on the server.<br />
                            To start the application on the CSRL Server, run the start script in the Digital Twin directory:
                        </p>
                        <div className="offline-path-box">
                            <code>D:\Digital_twin_dev\start.sh</code>
                            <span className="or-divider">or (on Linux/Ubuntu server)</span>
                            <code>./start.sh -y</code>
                        </div>
                        <div className="dt-ports-grid">
                            <div className="dt-port-card">
                                <span className="port-name">Frontend UI</span>
                                <span className="port-num">Port 5173</span>
                            </div>
                            <div className="dt-port-card">
                                <span className="port-name">Backend REST API</span>
                                <span className="port-num">Port 3001</span>
                            </div>
                            <div className="dt-port-card">
                                <span className="port-name">Grafana Dashboard</span>
                                <span className="port-num">Port 3005</span>
                            </div>
                            <div className="dt-port-card">
                                <span className="port-name">Embedded MQTT</span>
                                <span className="port-num">Port 1884</span>
                            </div>
                        </div>
                        <button
                            className="retry-btn"
                            onClick={() => {
                                setTwinOnline(null);
                                checkServer();
                            }}
                        >
                            🔄 Retry Connection
                        </button>
                    </div>
                ) : twinOnline === null ? (
                    <div className="dt-offline-screen">
                        <div className="offline-icon spin">⚙️</div>
                        <h3>Checking Digital Twin Server Status…</h3>
                    </div>
                ) : (
                    <div className="dt-iframe-wrapper">
                        <iframe
                            src="https://csrl.nitk.ac.in/digital-twin-app/"
                            width="100%"
                            height="900px"
                            style={{ border: 'none', borderRadius: '8px' }}
                            title="Digital Twin Engine Application"
                            allowFullScreen
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default DigitalTwin;
